import React, {useEffect, useState} from "react";
import {Modal, Space, Skeleton, Alert, Typography, Select, Input, InputNumber, Table, Button, Divider, message} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import api from "../api";
import { handleApiError } from "../helpers/formatApiError";
import RenamePresetEditor from "../manage/RenamePresetEditor";

const {Text} = Typography;

const BulkRenameModal = ({
    open,
    onCancel,
    onOk,
    assetIds = []
}) => {
    const {t} = useTranslation();
    const [presets, setPresets] = useState([]);
    const [loadingPresets, setLoadingPresets] = useState(true);
    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [customText, setCustomText] = useState('');
    const [customText2, setCustomText2] = useState('');
    const [startNumber, setStartNumber] = useState(1);
    const [previewData, setPreviewData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [duplicateResolution, setDuplicateResolution] = useState(null);
    const [editorVisible, setEditorVisible] = useState(false);
    const [globalStart, setGlobalStart] = useState(null);

    const loadPresets = () => {
        setLoadingPresets(true);
        api('/api/rename_presets?enabled=true').then(res => {
            setPresets(res.data);
            setLoadingPresets(false);
        }).catch(() => setLoadingPresets(false));
    };

    // Load presets when modal opens
    useEffect(() => {
        if (!open) return;
        loadPresets();
    }, [open]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setSelectedPresetId(null);
            setCustomText('');
            setCustomText2('');
            setStartNumber(1);
            setPreviewData(null);
            setDuplicateResolution(null);
            setGlobalStart(null);
        }
    }, [open]);

    const selectedPreset = presets.find(p => p.id === selectedPresetId);
    const template = selectedPreset?.template || [];

    const needsCustomText = template.some(s => s.token === 'custom_text');
    const needsCustomText2 = template.some(s => s.token === 'custom_text_2');
    const needsStartNumber = template.some(s => s.token === 'sequence_number_in_set');
    const needsGlobalStart = template.some(s => s.token === 'global_file_number');

    const loadPreview = () => {
        if (!selectedPresetId || assetIds.length === 0) return;
        setLoadingPreview(true);
        api.post('/api/bulk_jobs/rename_preview', {
            rename_preset_id: selectedPresetId,
            asset_ids: assetIds,
            custom_text: customText,
            custom_text_2: customText2,
            start_number: startNumber,
            global_start: globalStart
        }).then(res => {
            setPreviewData(res.data);
            setLoadingPreview(false);
        }).catch(() => setLoadingPreview(false));
    };

    // Auto-preview when preset changes
    useEffect(() => {
        if (selectedPresetId && open) {
            loadPreview();
        }
    }, [selectedPresetId]);

    const handleOk = () => {
        onOk({
            rename_preset_id: selectedPresetId,
            rename_custom_text: customText || null,
            rename_custom_text_2: customText2 || null,
            rename_start_number: startNumber,
            rename_duplicate_resolution: duplicateResolution,
            rename_global_start: globalStart
        });
    };

    const handleEditorSave = (values) => {
        api.post('/api/rename_presets', {rename_preset: values}).then(res => {
            message.success(t('preset-created', 'Preset created!'));
            setEditorVisible(false);
            loadPresets();
            setSelectedPresetId(res.data.id);
        }).catch(err => {
            handleApiError(err);
        });
    };

    const handleSelectChange = (value) => {
        if (value === '__new__') {
            setEditorVisible(true);
        } else {
            setSelectedPresetId(value);
        }
    };

    const hasMissingMetadata = previewData?.missing_metadata?.count > 0;
    const hasMissingDates = previewData?.missing_dates?.count > 0;
    const hasDuplicates = (previewData?.duplicates?.internal_count > 0) || (previewData?.duplicates?.existing_conflict_count > 0);

    const previewColumns = [
        {title: t('original','Original'), dataIndex: 'original', ellipsis: true},
        {title: t('renamed','Renamed'), dataIndex: 'renamed', ellipsis: true}
    ];

    const selectOptions = [
        ...presets.map(p => ({label: p.name, value: p.id})),
        {label: <><PlusOutlined/> {t('new-preset', 'New Preset...')}</>, value: '__new__'}
    ];

    return (
        <>
            <Modal
                title={t('rename-count-files', 'Rename {{count}} Files', {count: assetIds.length})}
                open={open}
                onCancel={onCancel}
                onOk={handleOk}
                okText={t('rename', 'Rename')}
                cancelText={t('cancel', 'Cancel')}
                okButtonProps={{disabled: !selectedPresetId || loadingPreview}}
                width={640}
                modalRender={(modal) => <div onClick={(e) => e.stopPropagation()}>{modal}</div>}
            >
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    <div>
                        <Text strong>{t('file-naming', 'File Naming')}:</Text>
                        <Select
                            style={{width: '100%', marginTop: 4}}
                            placeholder={t('select-preset', 'Select a rename preset...')}
                            value={selectedPresetId}
                            onChange={handleSelectChange}
                            loading={loadingPresets}
                            options={selectOptions}
                        />
                    </div>

                    {needsCustomText && (
                        <div>
                            <Text strong>{t('custom-text', 'Custom Text')}:</Text>
                            <Input
                                style={{marginTop: 4}}
                                value={customText}
                                onChange={e => setCustomText(e.target.value)}
                                onBlur={loadPreview}
                            />
                        </div>
                    )}

                    {needsCustomText2 && (
                        <div>
                            <Text strong>{t('custom-text-2', 'Custom Text 2')}:</Text>
                            <Input
                                style={{marginTop: 4}}
                                value={customText2}
                                onChange={e => setCustomText2(e.target.value)}
                                onBlur={loadPreview}
                            />
                        </div>
                    )}

                    {needsStartNumber && (
                        <div>
                            <Text strong>{t('start-number', 'Start Number')}:</Text>
                            <InputNumber
                                style={{marginTop: 4, marginLeft: 8}}
                                min={0}
                                value={startNumber}
                                onChange={val => setStartNumber(val || 1)}
                                onBlur={loadPreview}
                            />
                        </div>
                    )}

                    {needsGlobalStart && (
                        <div>
                            <Text strong>{t('global-start-number', 'Global Start Number')}:</Text>
                            <InputNumber
                                style={{marginTop: 4, marginLeft: 8}}
                                min={1}
                                value={globalStart}
                                onChange={val => setGlobalStart(val)}
                                onBlur={loadPreview}
                                placeholder={previewData?.next_global_number || t('default-continue', 'Continue from last')}
                            />
                            <div><Text type="secondary">{t('global-start-hint', 'Leave blank to continue from the last used number')}</Text></div>
                        </div>
                    )}

                    {selectedPresetId && (
                        <Button size="small" onClick={loadPreview} loading={loadingPreview}>
                            {t('refresh-preview', 'Refresh Preview')}
                        </Button>
                    )}

                    {loadingPreview && <Skeleton active paragraph={{rows: 3}}/>}

                    {previewData && !loadingPreview && (
                        <>
                            {previewData.previews.length > 0 && (
                                <div>
                                    <Text type="secondary">
                                        {t('example-preview', 'Example: {{original}} → {{renamed}}', {
                                            original: previewData.previews[0].original,
                                            renamed: previewData.previews[0].renamed
                                        })}
                                    </Text>
                                </div>
                            )}

                            {hasMissingMetadata && (
                                <Alert
                                    type="warning"
                                    message={t('missing-metadata-warning', 'Missing Metadata')}
                                    description={t('missing-metadata-desc',
                                        '{{count}} of your files do not have metadata used by this naming preset. If you choose to rename, that part of the name will be blank.',
                                        {count: previewData.missing_metadata.count}
                                    )}
                                />
                            )}

                            {hasMissingDates && (
                                <Alert
                                    type="error"
                                    message={t('missing-dates-warning', 'Missing Dates')}
                                    description={
                                        <Space direction="vertical" size="small">
                                            <Text>
                                                {t('missing-dates-desc',
                                                    '{{count}} files do not have a capture date. The date portion of the filename will be blank for these files:',
                                                    {count: previewData.missing_dates.count}
                                                )}
                                            </Text>
                                            <ul style={{margin: 0, paddingLeft: 20}}>
                                                {previewData.missing_dates.assets.map(a => (
                                                    <li key={a.id}><Text type="secondary">{a.filename}</Text></li>
                                                ))}
                                            </ul>
                                            {previewData.missing_dates.count > 20 && (
                                                <Text type="secondary">
                                                    {t('and-more', '...and {{count}} more', {count: previewData.missing_dates.count - 20})}
                                                </Text>
                                            )}
                                        </Space>
                                    }
                                />
                            )}

                            {hasDuplicates && (
                                <Alert
                                    type="warning"
                                    message={t('duplicate-filenames-warning', 'Duplicate File Names')}
                                    description={
                                        <Space direction="vertical" size="small">
                                            <Text>
                                                {t('duplicate-filenames-desc',
                                                    'Some filenames would conflict with existing files. You can create unique names by adding a suffix.',
                                                )}
                                            </Text>
                                            <Space>
                                                <Button
                                                    size="small"
                                                    type={duplicateResolution === 'unique_suffix' ? 'primary' : 'default'}
                                                    onClick={() => setDuplicateResolution('unique_suffix')}
                                                >
                                                    {t('create-unique-names', 'Create unique names with -#')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    type={duplicateResolution === null ? 'primary' : 'default'}
                                                    onClick={() => setDuplicateResolution(null)}
                                                >
                                                    {t('rename-anyway', 'Rename anyway')}
                                                </Button>
                                            </Space>
                                        </Space>
                                    }
                                />
                            )}

                            <Divider style={{margin: '8px 0'}}/>

                            <Table
                                size="small"
                                columns={previewColumns}
                                dataSource={previewData.previews}
                                rowKey="id"
                                pagination={false}
                                scroll={{y: 200}}
                            />

                            {previewData.total > previewData.previews.length && (
                                <Text type="secondary">
                                    {t('showing-of-total', 'Showing {{shown}} of {{total}} files', {
                                        shown: previewData.previews.length,
                                        total: previewData.total
                                    })}
                                </Text>
                            )}
                        </>
                    )}
                </Space>
            </Modal>

            <RenamePresetEditor
                visible={editorVisible}
                onClose={() => setEditorVisible(false)}
                onSave={handleEditorSave}
                editPreset={null}
            />
        </>
    );
};

export default BulkRenameModal;
